import { gradients, tokenColor } from '@gestao/ui'
import type { CSSProperties, Ref } from 'react'

import { appConfig } from '../../app/config'
import * as t from '../../i18n/pt-BR'

/*
 * Painel esquerdo do login (design-system §6): gradiente petróleo escuro, três formas
 * orgânicas desfocadas em jade e petróleo-claro, uma malha de linhas finas a 8% e, embaixo, o
 * nome do produto, a frase e "Uso interno • cliente". A malha repete a geometria da carteira:
 * linhas a cada 40 px (a altura de linha da tabela) e réguas verticais onde ficam as colunas.
 * As cores são fixas (não seguem o tema): o painel é o mesmo no claro e no escuro.
 */

const PANEL_BACKGROUND: CSSProperties = {
  backgroundImage: `linear-gradient(to top right, ${gradients.login.from} 0%, ${gradients.login.from} 30%, ${gradients.login.to} 100%)`,
}

/**
 * Formas: posição e tamanho relativos ao painel. Cores fixas do design system: jade (claro),
 * petróleo-claro (o petróleo do tema escuro, #35B5AF) e jade-claro.
 */
const SHAPES = [
  {
    className: 'top-[-14%] left-[44%] w-[58%] opacity-60',
    color: tokenColor('jade', 'light'),
    radius: '58% 42% 55% 45% / 46% 52% 48% 54%',
  },
  {
    className: 'top-[18%] left-[-8%] w-[46%] opacity-40',
    color: tokenColor('petroleo', 'dark'),
    radius: '41% 59% 38% 62% / 55% 37% 63% 45%',
  },
  {
    className: 'top-[46%] left-[58%] w-[36%] opacity-30',
    color: tokenColor('jade', 'dark'),
    radius: '63% 37% 49% 51% / 42% 61% 39% 58%',
  },
] as const

/** Réguas verticais da malha: limites das colunas da carteira (cliente, cobradora, atraso...). */
const COLUMN_RULES = ['31%', '49%', '61%', '75%', '88%']

export interface LoginHeroRefs {
  shapesRef: Ref<HTMLDivElement>
  copyRef: Ref<HTMLDivElement>
}

export function LoginHero({ shapesRef, copyRef }: LoginHeroRefs) {
  return (
    <header
      className="relative isolate flex h-40 shrink-0 flex-col justify-end overflow-hidden px-6 py-5 text-white login:h-auto login:w-[58%] login:max-w-[1100px] login:px-16 login:py-16"
      style={PANEL_BACKGROUND}
    >
      <div ref={shapesRef} aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        {SHAPES.map((shape) => (
          <div
            key={shape.radius}
            data-shape
            className={`absolute aspect-square blur-[72px] will-change-transform ${shape.className}`}
            style={{ borderRadius: shape.radius, backgroundColor: shape.color }}
          />
        ))}
      </div>

      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 size-full text-white opacity-8"
      >
        <defs>
          <pattern id="login-rows" width="40" height="40" patternUnits="userSpaceOnUse">
            <line x1="0" y1="39.5" x2="40" y2="39.5" stroke="currentColor" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#login-rows)" />
        {COLUMN_RULES.map((x) => (
          <line key={x} x1={x} y1="0" x2={x} y2="100%" stroke="currentColor" strokeWidth="1" />
        ))}
      </svg>

      <div ref={copyRef} className="flex max-w-[34rem] flex-col gap-1 login:gap-3">
        <p translate="no" className="text-kpi text-balance login:text-display">
          {appConfig.appName}
        </p>
        <p className="text-compact text-white/85 login:text-body-lg">{t.auth.tagline}</p>
        <p className="mt-1 text-compact text-white/70 login:mt-2">
          {t.auth.internalUse(appConfig.clientName)}
        </p>
      </div>
    </header>
  )
}
